import {
  memo, useMemo, useRef,
} from '../../lib/teact/teact';
import { getGlobal } from '../../global';

import type {
  ApiFormattedText, ApiMessage, ApiMessageEntity, ApiStory,
} from '../../api/types';
import type { ObserveFn } from '../../hooks/useIntersectionObserver';
import type { ThreadId } from '../../types';
import { ApiMessageEntityTypes } from '../../api/types';

import { setTokenPillContext } from '../../lib/crypto/TokenPillContext';
import { extractMessageText, getUserFullName, stripCustomEmoji } from '../../global/helpers';
import { selectChat, selectUser } from '../../global/selectors';
import trimText from '../../util/trimText';
import { insertTextEntity, renderTextWithEntities } from './helpers/renderTextWithEntities';

import useLang from '../../hooks/useLang';
import useLastCallback from '../../hooks/useLastCallback';
import useSyncEffect from '../../hooks/useSyncEffect';
import useUniqueId from '../../hooks/useUniqueId';

import TypingWrapper from './TypingWrapper';

interface OwnProps {
  messageOrStory: ApiMessage | ApiStory;
  threadId?: ThreadId;
  forcedText?: ApiFormattedText;
  isForAnimation?: boolean;
  emojiSize?: number;
  highlight?: string;
  asPreview?: boolean;
  truncateLength?: number;
  isProtected?: boolean;
  observeIntersectionForLoading?: ObserveFn;
  observeIntersectionForPlaying?: ObserveFn;
  withTranslucentThumbs?: boolean;
  shouldRenderAsHtml?: boolean;
  inChatList?: boolean;
  forcePlayback?: boolean;
  focusedQuote?: string;
  focusedQuoteOffset?: number;
  isInSelectMode?: boolean;
  canBeEmpty?: boolean;
  maxTimestamp?: number;
  shouldAnimateTyping?: boolean;
  canAnimateTextStreaming?: boolean;
  onTypingAnimationEnd?: NoneToVoidFunction;
}

const MIN_CUSTOM_EMOJIS_FOR_SHARED_CANVAS = 3;

function MessageText({
  messageOrStory,
  forcedText,
  isForAnimation,
  emojiSize,
  highlight,
  asPreview,
  truncateLength,
  isProtected,
  observeIntersectionForLoading,
  observeIntersectionForPlaying,
  withTranslucentThumbs,
  shouldRenderAsHtml,
  inChatList,
  forcePlayback,
  focusedQuote,
  focusedQuoteOffset,
  isInSelectMode,
  canBeEmpty,
  maxTimestamp,
  threadId,
  shouldAnimateTyping,
  canAnimateTextStreaming,
  onTypingAnimationEnd,
}: OwnProps) {
  const sharedCanvasRef = useRef<HTMLCanvasElement>();
  const sharedCanvasHqRef = useRef<HTMLCanvasElement>();

  const textCacheBusterRef = useRef(0);

  const lang = useLang();

  const formattedText = forcedText || extractMessageText(messageOrStory, inChatList);
  const adaptedFormattedText = isForAnimation && formattedText ? stripCustomEmoji(formattedText) : formattedText;
  const { text, entities } = adaptedFormattedText || {};

  const entitiesWithFocusedQuote = useMemo(() => {
    if (!text || !focusedQuote) return entities;

    const offsetIndex = text.indexOf(focusedQuote, focusedQuoteOffset);
    const index = offsetIndex >= 0 ? offsetIndex : text.indexOf(focusedQuote); // Fallback to first occurrence
    const lendth = focusedQuote.length;
    if (index >= 0) {
      return insertTextEntity(entities || [], {
        offset: index,
        length: lendth,
        type: ApiMessageEntityTypes.QuoteFocus,
      });
    }

    return entities;
  }, [text, entities, focusedQuote, focusedQuoteOffset]);

  const containerId = useUniqueId();

  useSyncEffect(() => {
    textCacheBusterRef.current += 1;
  }, [text, entitiesWithFocusedQuote]);

  const withSharedCanvas = useMemo(() => {
    if (shouldAnimateTyping) {
      return false;
    }

    const customEmojisCount = countCustomEmojis(entitiesWithFocusedQuote);
    return customEmojisCount >= MIN_CUSTOM_EMOJIS_FOR_SHARED_CANVAS
      && !hasSpoileredCustomEmojis(entitiesWithFocusedQuote);
  }, [entitiesWithFocusedQuote, shouldAnimateTyping]);

  const renderText = useLastCallback((t: ApiFormattedText) => {
    const global = getGlobal();
    const senderId = 'senderId' in messageOrStory ? messageOrStory.senderId : undefined;
    const chatId = 'chatId' in messageOrStory ? messageOrStory.chatId : undefined;
    const user = senderId ? selectUser(global, senderId) : undefined;
    const senderChat = senderId ? selectChat(global, senderId) : undefined;
    const chat = chatId ? selectChat(global, chatId) : undefined;

    const senderName = user ? getUserFullName(user) : (senderChat?.title || chat?.title);
    const chatTitle = chat?.title;

    // Set context so TokenPill can access sender/chat info during render
    setTokenPillContext({
      senderId,
      senderName,
      chatId,
      chatTitle,
      messageDate: messageOrStory.date,
      messageId: messageOrStory.id,
    });

    const result = renderTextWithEntities({
      text: t.text,
      entities: t.entities,
      highlight,
      emojiSize,
      shouldRenderAsHtml,
      containerId,
      asPreview,
      isProtected,
      observeIntersectionForLoading,
      observeIntersectionForPlaying,
      withTranslucentThumbs,
      sharedCanvasRef: withSharedCanvas ? sharedCanvasRef : undefined,
      sharedCanvasHqRef: withSharedCanvas ? sharedCanvasHqRef : undefined,
      cacheBuster: textCacheBusterRef.current.toString(),
      forcePlayback,
      isInSelectMode,
      maxTimestamp,
      chatId: 'chatId' in messageOrStory ? messageOrStory.chatId : undefined,
      messageId: messageOrStory.id,
      threadId,
    });

    setTokenPillContext(undefined);
    return result;
  });

  if (!text && !canBeEmpty) {
    return <span className="content-unsupported">{lang('MessageUnsupported')}</span>;
  }

  const textToRender: ApiFormattedText = {
    text: trimText(text || '', truncateLength),
    entities: entitiesWithFocusedQuote,
  };
  const shouldRenderTypingPlaceholder = !('previousLocalId' in messageOrStory) || !messageOrStory.previousLocalId;

  return (
    <>
      {[
        withSharedCanvas && <canvas key="shared-canvas" ref={sharedCanvasRef} className="shared-canvas" />,
        withSharedCanvas && <canvas key="shared-canvas-hq" ref={sharedCanvasHqRef} className="shared-canvas" />,
        shouldAnimateTyping ? (
          <TypingWrapper
            key="typing-wrapper"
            formattedText={textToRender}
            renderText={renderText}
            shouldAnimateMask={canAnimateTextStreaming}
            shouldRenderPlaceholder={shouldRenderTypingPlaceholder}
            onCompleted={onTypingAnimationEnd}
            completionKey={messageOrStory.id}
          />
        ) : renderText(textToRender),
      ].flat().filter(Boolean)}
    </>
  );
}

function countCustomEmojis(entities?: ApiMessageEntity[]) {
  return entities?.filter((entity) => entity.type === ApiMessageEntityTypes.CustomEmoji).length || 0;
}

function hasSpoileredCustomEmojis(entities?: ApiMessageEntity[]) {
  const spoilers = entities?.filter((entity) => entity.type === ApiMessageEntityTypes.Spoiler);
  if (!spoilers?.length) {
    return false;
  }

  return Boolean(entities?.some((entity) => {
    if (entity.type !== ApiMessageEntityTypes.CustomEmoji) {
      return false;
    }

    return spoilers.some((spoiler) => {
      return entity.offset >= spoiler.offset
        && entity.offset + entity.length <= spoiler.offset + spoiler.length;
    });
  }));
}

export default memo(MessageText);
